import { Auth } from 'aws-amplify'
import { config } from '../../constants'
import http, {
  httpCustomInstance,
  httpCustomSummarizedInstance
} from './http'

const AUTH_STORAGE_KEYS = [
  'refreshToken',
  'accessToken',
  'idToken',
  'LastAuthUser'
]

const AUTH_STORAGE_KEY_PATTERNS = [
  'CognitoIdentityServiceProvider',
  'aws.cognito',
  'amplify-signin-with-hostedUI',
  'oauth_state',
  'oauth_pkce_key',
  'federatedInfo'
]

const isAuthStorageKey = key =>
  AUTH_STORAGE_KEYS.includes(key) ||
  AUTH_STORAGE_KEY_PATTERNS.some(pattern => key.includes(pattern))

const removeAuthStorageItems = storage => {
  if (!storage) return

  try {
    AUTH_STORAGE_KEYS.forEach(key => storage.removeItem(key))

    Array.from({ length: storage.length }, (_, index) => storage.key(index))
      .filter(Boolean)
      .filter(isAuthStorageKey)
      .forEach(key => storage.removeItem(key))
  } catch {}
}

function clearAuthStorage() {
  removeAuthStorageItems(
    typeof localStorage !== 'undefined' ? localStorage : null
  )
  removeAuthStorageItems(
    typeof sessionStorage !== 'undefined' ? sessionStorage : null
  )
}

async function clearAuthSession() {
  try {
    await Auth.signOut({ global: true })
  } catch {}

  clearAuthStorage()
}

class shlStorage {
  static getKey(keyValue) {
    const accessItem = AUTH_STORAGE_KEYS.find(item => keyValue.includes(item))
    return accessItem
  }

  static setItem(key, value) {
    const accessItem = this.getKey(key)
    localStorage.setItem(accessItem || key, value)
  }

  // get item with the key
  static getItem(key) {
    const accessItem = this.getKey(key)
    const item = localStorage.getItem(accessItem || key)
    return item
  }

  // remove item with the key
  static removeItem(key) {
    const item = this.getKey(key)
    localStorage.removeItem(item || key)
  }

  // clear out the storage
  static clear() {
    clearAuthStorage()
  }
}

Auth.configure({
  storage: shlStorage,
  Auth: {
    region: config.cognito.REGION,
    userPoolId: config.cognito.USER_POOL_ID,
    userPoolWebClientId: config.cognito.USER_POOL_WEB_CLIENT_ID,
    mandatorySignIn: config.cognito.MANDATORY_SIGN_IN,
    authenticationFlowType: config.cognito.AUTHENTICATION_FLOW_TYPE,
    identityPoolId: config.cognito.IDENTITY_POOL_ID
  }
})

const verifyPasswordResetCode = data => {
  data.ClientId = config.cognito.USER_POOL_WEB_CLIENT_ID
  return http({
    method: 'POST',
    url: `/verify-user-email`,
    data
  })
}

function getPasswordResetCode(email) {
  return Auth.forgotPassword(email)
}

function passwordReset(values) {
  return Auth.forgotPasswordSubmit(
    values.username,
    values.code,
    values.password
  )
}

async function signIn(email, password, captchaToken) {
  return Auth.signIn(email, password,
  //    {
  //   clientMetadata: {
  //     captcha: captchaToken
  //   }
  // }
)
}

// keys are accessToken, idToken and refreshToken....if no key is passed, take accessToken by default
const getToken = async (key) => {
  try {
    const session = await Auth.currentSession()
    if (key) {
      const tokenObj = session[key]
      const token = tokenObj.jwtToken || tokenObj.token
      return token
    } else {
      const token = session.idToken.jwtToken
      return token
    }

  } catch {
    return null
  }
}

// Fetch refresh token specifically
const fetchRefreshToken = async () => {
  try {
    const session = await Auth.currentSession()
    const refreshToken = session.getRefreshToken().getToken()
    return refreshToken
  } catch (error) {
    console.error('Error fetching refresh token:', error)
    return null
  }
}

// Fetch all tokens (idToken, accessToken, refreshToken)
const fetchAllTokens = async () => {
  try {
    const session = await Auth.currentSession()
    return {
      idToken: session.getIdToken().getJwtToken(),
      accessToken: session.getAccessToken().getJwtToken(),
      refreshToken: session.getRefreshToken().getToken()
    }
  } catch (error) {
    console.error('Error fetching all tokens:', error)
    return null
  }
}

function deleteAllCookies() {
  document.cookie.split(';').forEach(function (c) {
    document.cookie = c
      .replace(/^ +/, '')
      .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/')
  })
}

function signUp(values) {
  const { username, password, ...rest } = values
  const params = {
    username,
    password,
    attributes: rest
  }
  console.log(params, 'paramas datas..')
  return Auth.signUp(params)
}

function signOut() {
  return clearAuthSession()
}

function confirmSignUp(values) {
  return Auth.confirmSignUp(values.username, values.code)
}

async function getSession() {
  try {
    const session = await Auth.currentSession();
    return session;
  } catch (err) {
    await clearAuthSession()
    throw err; // throw so Axios interceptor knows to cancel
  }
}

function resendConfirmationCode(username) {
  return Auth.resendSignUp(username)
}

async function getUser() {
  return Promise.all([Auth.currentUserInfo(), getDbUser()])
    .then(async ([user, dbUser]) => {
      if (!user || !user.attributes) {
        await clearAuthSession()
        return null
      }

      const {
        'custom:country': country,
        'custom:position': position,
        'custom:timezone': timezone,
        address,
        phone_number: phoneNumber,
        'custom:linkedin': linkedin,
        'custom:bio': bio,
        name
      } = user.attributes;

      return {
        ...dbUser.data,
        name,
        address,
        phone_number: phoneNumber,
        country,
        position,
        timezone,
        bio,
        linkedin
      };
    });
}

function updateUser(id, attributes, dbAttributes) {
  return Promise.resolve()
    .then(() => {
      if (attributes) {
        return Auth.currentAuthenticatedUser().then(user =>
          Auth.updateUserAttributes(user, attributes)
        )
      }
      return null
    })
    .then(() => {
      if (dbAttributes) {
        return updateDbUser(id, dbAttributes)
      }
      return null
    })
}

function authenticate() {
  // deleteAllCookies()
  return Auth.currentAuthenticatedUser({
    bypassCache: false
  })
}

const getDbUser = (id = 'me') => {
  return http({
    method: 'GET',
    url: `/users/${id}`
  })
}

const updateDbUser = (id, data) => {
  return http({
    method: 'PUT',
    url: `/users/${id}`,
    data
  })
}

const inviteUser = token => {
  return http({
    method: 'GET',
    url: `/users/invite?token=${token}`
  })
}

const resetUserFlag = () => {
  return http({
    method: 'PUT',
    url: `/users/flag`
  })
}

const stripeConnect = (id, pathName) => {
  return http({
    method: 'POST',
    url: `/users/${id}/connect`,
    data: {
      path_name: pathName
    }
  })
}

const uploadMassUser = data => {
  return http({
    method: 'POST',
    url: '/users/import_users',
    data
  })
}

const sendCertificateByEmail = params => {
  return http({
    method: 'POST',
    url: `/sendCert/${params.course_id}/${params.library_id}`
  })
}

const updateScheduleTime = data => {
  return httpCustomInstance({
    method: 'PUT',
    url: `/ondemand/schedule`,
    data
  })
}

const getScheduleTime = () => {
  return httpCustomInstance({
    method: 'GET',
    url: `/ondemand/schedule`
  })
}

const getIntegrationStatus = () => {
  return http({
    method: 'GET',
    url: '/vendor/zoom/integration/any'
  })
}

const revokeIntegration = () => {
  return http({
    method: 'DELETE',
    url: '/vendor/zoom/integration/revoke'
  })
}

const getSharelookVoices = () => {
  return httpCustomSummarizedInstance({
    method: 'GET',
    url: `nvidia-english/`
  })
}

const getEnglishAudioPreview = data => {
  return httpCustomSummarizedInstance({
    method: 'POST',
    url: `nvidia-english/tts`,
    data
  })
}

const getNonEnglishAudioPreview = data => {
  return httpCustomSummarizedInstance({
    method: 'POST',
    url: `non-english-coqui-tts/non_english_tts`,
    data
  })
}

const deleteUser = id => {
  return http({
    method: 'DELETE',
    url: `/users/${id}`
  })
}

export {
  getPasswordResetCode,
  getToken,
  fetchRefreshToken,
  fetchAllTokens,
  clearAuthSession,
  passwordReset,
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  authenticate,
  getSession,
  getUser,
  updateUser,
  deleteUser,
  updateDbUser,
  getDbUser,
  inviteUser,
  resetUserFlag,
  uploadMassUser,
  sendCertificateByEmail,
  updateScheduleTime,
  getScheduleTime,
  getSharelookVoices,
  getIntegrationStatus,
  revokeIntegration,
  resendConfirmationCode,
  getEnglishAudioPreview,
  getNonEnglishAudioPreview,
  stripeConnect,
  verifyPasswordResetCode
}
